import React from 'react';
import { FeatureIconVisual } from './FeatureIconVisual';
import {
  getFeaturesGridColumns,
  hasFeatureIcon,
  normalizeFeaturesGridContent,
  resolveFeaturesGridVariant,
  type FeaturesGridLayoutVariant,
} from '../utils/featuresGridUtils';
import {
  getFeaturesGridLayoutMeta,
  getFeaturesGridBodyPadding,
  getFeaturesGridTitlePadding,
} from '../utils/featuresGridLayouts';

type FeatureItem = {
  icon?: string;
  iconUrl?: string;
  title?: string;
  description?: string;
  enabled?: boolean;
};

export const FeaturesGridBlock: React.FC<{
  content: Record<string, unknown>;
  styles: Record<string, unknown>;
  isMobile?: boolean;
  computePadding: (styles: Record<string, unknown>, fallback: string) => string;
}> = ({ content, styles, isMobile = false, computePadding }) => {
  const gridContent = normalizeFeaturesGridContent(content);
  const sectionTitle =
    typeof gridContent.sectionTitle === 'string' ? gridContent.sectionTitle.trim() : '';
  const features = (Array.isArray(gridContent.features)
    ? gridContent.features
    : []
  ).filter((f: FeatureItem) => f && f.enabled !== false) as FeatureItem[];

  const variant = resolveFeaturesGridVariant(gridContent);
  const meta = getFeaturesGridLayoutMeta(variant);
  const columns = getFeaturesGridColumns(variant, isMobile);
  const isDark = variant === 'dark-band';
  const bgColor = isDark ? '#111827' : (styles.backgroundColor as string) || '#FFFFFF';
  const titleColor = isDark ? '#F9FAFB' : '#111827';
  const bodyTitleColor = isDark ? '#F9FAFB' : '#111827';
  const bodyDescColor = isDark ? '#9CA3AF' : variant === 'cards' ? '#64748B' : '#374151';
  const titlePad = getFeaturesGridTitlePadding(variant, isMobile);
  const bodyPad = getFeaturesGridBodyPadding(variant, isMobile);
  const maxContentWidth = variant === 'icon-list' ? '720px' : variant === 'compact-4' ? '100%' : '1100px';

  const titleFontSize =
    variant === 'icon-2x2' || variant === 'icon-list'
      ? isMobile
        ? '28px'
        : '36px'
      : variant === 'compact-4' || variant === 'numbered'
        ? isMobile
          ? '26px'
          : '32px'
        : isMobile
          ? '32px'
          : '42px';

  const renderIconBox = (
    feature: FeatureItem,
    size: number,
    box: React.CSSProperties
  ) =>
    hasFeatureIcon(feature) ? (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          ...box,
        }}
      >
        <FeatureIconVisual feature={feature} size={size} emojiFontSize={size} />
      </div>
    ) : null;

  const renderTitle = (feature: FeatureItem, size?: string, mb?: string) =>
    feature.title ? (
      <div
        style={{
          fontSize: size || (isMobile ? '16px' : '17px'),
          fontWeight: 700,
          color: bodyTitleColor,
          marginBottom: mb || '8px',
          letterSpacing: '-0.01em',
          lineHeight: 1.3,
        }}
      >
        {feature.title}
      </div>
    ) : null;

  const renderDesc = (feature: FeatureItem, size?: string) =>
    feature.description ? (
      <div
        style={{
          fontSize: size || (isMobile ? '14px' : '15px'),
          lineHeight: 1.55,
          color: bodyDescColor,
          fontWeight: 400,
        }}
      >
        {feature.description}
      </div>
    ) : null;

  const renderFeature = (feature: FeatureItem, index: number) => {
    switch (variant as FeaturesGridLayoutVariant) {
      case 'icon-list':
        return (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '16px',
              width: '100%',
              marginBottom: isMobile ? '24px' : '28px',
            }}
          >
            {renderIconBox(feature, 26, {
              width: '48px',
              height: '48px',
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
            })}
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              {renderTitle(feature)}
              {renderDesc(feature)}
            </div>
          </div>
        );

      case 'numbered': {
        const stepLabel = String(index + 1).padStart(2, '0');
        return (
          <div key={index} style={{ textAlign: 'left', width: '100%' }}>
            <div style={{ marginBottom: '12px' }}>
              {hasFeatureIcon(feature) ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  {renderIconBox(feature, 28, {
                    width: '56px',
                    height: '56px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                  })}
                  <span
                    style={{
                      position: 'absolute',
                      top: '-6px',
                      left: '-6px',
                      fontSize: '11px',
                      fontWeight: 700,
                      color: '#6B7280',
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      padding: '2px 6px',
                      lineHeight: 1.2,
                    }}
                  >
                    {stepLabel}
                  </span>
                </div>
              ) : (
                <div
                  style={{
                    fontSize: isMobile ? '32px' : '40px',
                    fontWeight: 800,
                    color: '#E5E7EB',
                    lineHeight: 1,
                    letterSpacing: '-0.03em',
                  }}
                >
                  {stepLabel}
                </div>
              )}
            </div>
            {renderTitle(feature, isMobile ? '17px' : '18px', '8px')}
            {renderDesc(feature)}
          </div>
        );
      }

      case 'compact-4':
        return (
          <div
            key={index}
            style={{
              textAlign: 'center',
              padding: isMobile ? '8px 6px' : '0 10px',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {renderIconBox(feature, 22, {
              width: '44px',
              height: '44px',
              margin: '0 auto 14px auto',
              backgroundColor: '#F9FAFB',
              borderRadius: '50%',
              border: '1px solid #E5E7EB',
            })}
            {renderTitle(feature, isMobile ? '14px' : '15px', '6px')}
            {renderDesc(feature, isMobile ? '12px' : '13px')}
          </div>
        );

      case 'dark-band':
        return (
          <div
            key={index}
            style={{
              textAlign: 'center',
              padding: isMobile ? '28px 20px' : '32px 24px',
              backgroundColor: '#1F2937',
              borderRadius: '16px',
              border: '1px solid #374151',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {renderIconBox(feature, 26, {
              width: '52px',
              height: '52px',
              margin: '0 auto 16px auto',
              backgroundColor: '#111827',
              borderRadius: '14px',
              border: '1px solid #374151',
            })}
            {renderTitle(feature, isMobile ? '17px' : '18px', '8px')}
            {renderDesc(feature)}
          </div>
        );

      case 'icon-2x2':
        return (
          <div key={index} style={{ textAlign: 'left', width: '100%', boxSizing: 'border-box' }}>
            {renderIconBox(feature, 28, {
              marginBottom: '16px',
              width: '56px',
              height: '56px',
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
            })}
            {renderTitle(feature, isMobile ? '17px' : '18px')}
            {renderDesc(feature)}
          </div>
        );

      case 'cards':
      default:
        return (
          <div
            key={index}
            style={{
              textAlign: 'center',
              padding: isMobile ? '32px 24px' : '40px 32px',
              width: '100%',
              boxSizing: 'border-box',
              backgroundColor: '#F9FAFB',
              borderRadius: '24px',
              border: '1px solid #F1F5F9',
            }}
          >
            {renderIconBox(feature, 32, {
              marginBottom: '20px',
              width: '64px',
              height: '64px',
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)',
            })}
            {renderTitle(feature, isMobile ? '18px' : '20px', '12px')}
            {renderDesc(feature)}
          </div>
        );
    }
  };

  const gridGap =
    variant === 'icon-2x2'
      ? { rowGap: isMobile ? '32px' : '48px', columnGap: isMobile ? '24px' : '40px' }
      : variant === 'numbered'
        ? { gap: isMobile ? '28px' : '40px' }
        : variant === 'compact-4'
          ? { gap: isMobile ? '20px 12px' : '32px 24px' }
          : { gap: isMobile ? '24px' : '32px' };

  return (
    <div
      style={{
        padding: computePadding(styles, '0px'),
        backgroundColor: bgColor,
        fontFamily: (styles.fontFamily as string) || "'Inter', 'system-ui', sans-serif",
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {sectionTitle ? (
        <div
          style={{
            padding: titlePad,
            fontSize: titleFontSize,
            fontWeight: meta.titleWeight,
            color: titleColor,
            textAlign: meta.titleAlign,
            maxWidth: variant === 'icon-list' ? maxContentWidth : '100%',
            margin: meta.titleAlign === 'center' ? '0 auto' : 0,
            width: '100%',
            boxSizing: 'border-box',
            fontFamily: meta.titleFontFamily,
            letterSpacing: '-0.02em',
            lineHeight: 1.15,
          }}
        >
          {sectionTitle}
        </div>
      ) : null}

      {features.length > 0 ? (
        <div
          style={{
            ...(variant === 'icon-list'
              ? {
                  display: 'block',
                  maxWidth: maxContentWidth,
                  margin: '0 auto',
                  padding: bodyPad,
                  width: '100%',
                  boxSizing: 'border-box',
                }
              : {
                  display: 'grid',
                  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                  ...gridGap,
                  padding: bodyPad,
                  maxWidth: maxContentWidth,
                  margin: '0 auto',
                  width: '100%',
                  boxSizing: 'border-box',
                }),
          }}
        >
          {features.map((feature, index) => renderFeature(feature, index))}
        </div>
      ) : null}
    </div>
  );
};
